// src/components/healthrecords/HealthRecordsTab.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { healthRecordAPI } from '../../services/api';
import { useAutoTriage, useInflightTriage, TRIAGE_STAGES, isScanImage } from './useAutoTriage';
import PhotoQualityNotice, { usePhotoQuality } from './PhotoQualityNotice';
import PhotoCapture from './PhotoCapture';
import IdentityMismatchModal from './IdentityMismatchModal';
import { RECORD_TYPES, getTypeConfig } from '../../constants/recordTypes';
import EmptyState from '../EmptyState';
import Icon from '../Icon';
import HeldReportNotice from '../HeldReportNotice';

// ── Constants ─────────────────────────────────────────────────────────────────


const STATUS_CONFIG = {
  UPLOADED:             { label: 'Uploaded',      color: 'var(--text-secondary)', bg: 'var(--bg-subtle)' },
  EXTRACTING:           { label: 'Extracting…',   color: 'var(--amber-text)', bg: 'var(--amber-bg)' },
  EXTRACTED:            { label: 'Ready',         color: 'var(--green)', bg: 'var(--green-bg)' },
  TRIAGED:              { label: 'Triaged',       color: 'var(--blue)', bg: 'var(--blue-bg)' },
  NEEDS_MANUAL_REVIEW:  { label: 'Needs Review',  color: 'var(--red)', bg: 'var(--red-bg)' },
  FAILED:               { label: 'Triage Failed', color: 'var(--red)', bg: 'var(--red-bg)' },
  AWAITING_IDENTITY_CONFIRMATION: { label: 'Needs Confirmation', color: 'var(--red)', bg: 'var(--red-bg)' },
  READING_SCAN:         { label: 'Reading scan…', color: 'var(--amber-text)', bg: 'var(--amber-bg)' },
  AWAITING_NAVIGATOR_QC: { label: 'Quality check', color: 'var(--purple)', bg: 'var(--purple-bg)' },
};

const RISK_CONFIG = {
  LOW:      { label: 'Low Risk',  color: 'var(--green)', bg: 'var(--green-bg)', border: 'var(--green)' },
  MODERATE: { label: 'Moderate',  color: 'var(--amber-text)', bg: 'var(--amber-bg)', border: 'var(--amber)' },
  HIGH:     { label: 'High Risk', color: 'var(--red)', bg: 'var(--red-bg)', border: 'var(--red)' },
  UNKNOWN:  { label: 'Unknown',   color: 'var(--text-secondary)', bg: 'var(--bg-subtle)', border: 'var(--border)' },
};

const MAX_FILE_SIZE_MB = 50;

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.UPLOADED;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 10px',
      borderRadius: 100, fontSize: 11, fontWeight: 700,
      fontFamily: "'DM Sans', sans-serif",
      color: cfg.color, background: cfg.bg,
    }}>
      {cfg.label}
    </span>
  );
}

function RiskBadge({ triage }) {
  if (!triage) return null;
  const cfg = RISK_CONFIG[triage.consensusRiskLevel] || RISK_CONFIG.UNKNOWN;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 10px', borderRadius: 100,
      fontSize: 11, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
    }}>
      <Icon name={triage.hasAcuteAlert ? 'alert' : 'heart'} size={13} /> {cfg.label}
    </span>
  );
}

// ── Server-side triage progress ──────────────────────────────────────────────
// A record the server is triaging (a re-run, or a report released after a quality check) has processingStatus EXTRACTING / READING_SCAN.
// Because that lives on the server, this shows the same progress after the tab was closed or the navigator came back from another screen.
const SERVER_RUNNING = ['EXTRACTING', 'READING_SCAN'];

function ServerTriageBanner() {
  return (
    <div role="status" style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
      background: 'var(--amber-bg)', border: '1px solid var(--amber)', borderRadius: 8, fontSize: 12.5, marginTop: 8,
    }}>
      <span style={{ fontSize: 16, flexShrink: 0 }}><Icon name="clock" size={16} /></span>
      <div style={{ flex: 1 }}>
        <span style={{ fontWeight: 600, color: 'var(--amber-text)' }}>Triage in progress…</span>
        <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>Running on the server. You can leave this page; it will show here when it finishes.</span>
        <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>This can take a few minutes. Please check back after some time.</div>
        <div className="progress-indeterminate" />
      </div>
    </div>
  );
}

// ── Triage Progress Banner ───────────────────────────────────────────────────────
// Shown inline below the record row while auto-triage is running

function TriageProgressBanner({ recordId, stage, triageError }) {
  if (!recordId || stage === 'IDLE' || stage === 'SKIPPED') return null;

  const isDone   = stage === 'DONE';
  const isHeld   = stage === 'HELD';
  const isFailed = stage === 'FAILED';
  const info     = TRIAGE_STAGES[stage] || TRIAGE_STAGES.IDLE;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 14px',
      background: isDone ? 'var(--green-bg)' : isFailed ? 'var(--red-bg)' : isHeld ? 'var(--blue-bg)' : 'var(--amber-bg)',
      border: `1px solid ${isDone ? 'var(--green)' : isFailed ? 'var(--red)' : isHeld ? 'var(--blue)' : 'var(--amber)'}`,
      borderRadius: 8, fontSize: 12.5, marginTop: 8,
    }}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>
        <Icon name={isDone ? 'check' : isFailed ? 'alert' : 'clock'} size={16} />
      </span>
      <div style={{ flex: 1 }}>
        <span style={{ fontWeight: 600, color: isDone ? 'var(--green)' : isFailed ? 'var(--red)' : isHeld ? 'var(--blue)' : 'var(--amber-text)' }}>
          {isDone ? 'Auto-triage complete' : isFailed ? 'Auto-triage failed' : isHeld ? 'Held for a navigator check' : 'Auto-triaging…'}
        </span>
        <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>
          {isFailed ? (triageError || 'Unknown error') : info.label}
        </span>
        {!isDone && !isFailed && !isHeld && (
          <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>This can take a few minutes. Please check back after some time.</div>
        )}
        {!isDone && !isFailed && !isHeld && (
          <div style={{ marginTop: 4, height: 3, background: 'var(--bg-hover)', borderRadius: 100, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 100,
              width: `${(info.step / 6) * 100}%`,
              background: 'var(--brand-accent)',
              transition: 'width 0.4s ease',
            }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Upload Modal ──────────────────────────────────────────────────────────────

function UploadModal({ patientId, patient, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [recordType, setRecordType] = useState('BLOOD_TEST');
  const [drawDate, setDrawDate] = useState('');
  const [labName, setLabName] = useState('');
  const [notes, setNotes] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();
  const photoProblems = usePhotoQuality(file, recordType);
  const [capturing, setCapturing] = useState(false);

  // Lock body scroll when modal open — iOS Safari needs both overflow AND position
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  // FIX: Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`File too large — max ${MAX_FILE_SIZE_MB}MB`);
      return;
    }
    setFile(f);
    setError('');
  };

  const handleSubmit = async () => {
    if (!file)     { setError('Please select a file'); return; }
    if (!drawDate) { setError('Please enter the test / visit date'); return; }
    setUploading(true);
    setError('');
    try {
      const res = await healthRecordAPI.upload(patientId, file, {
        recordType,
        drawDate,
        labName: labName || undefined,
        notes: notes || undefined,
      });
      onUploaded(res.data, file);  // pass file so auto-triage can extract text
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || 'Upload failed — please try again');
    } finally {
      setUploading(false);
    }
  };

  const modalContent = (
    // Overlay: fixed, covers viewport, centres card with flex.
    // Does NOT scroll — the card inner body scrolls instead.
    // This is the only pattern that works reliably on iOS Safari + Android Chrome.
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(13,30,53,0.55)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        boxSizing: 'border-box',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 14,
          width: '100%',
          maxWidth: 500,
          // maxHeight keeps card inside viewport; inner body scrolls
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(13,30,53,0.25)',
          overflow: 'hidden',
        }}>

        {/* Header — fixed inside card, never scrolls away */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 22px 14px',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--brand-accent)', marginBottom: 2 }}>
              UPLOAD DOCUMENT
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--text-primary)' }}>
              Add Health Record
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 1, padding: 4 }}>✕</button>
        </div>

        {/* Body — this part scrolls if content overflows */}
        <div style={{ padding: '18px 22px', overflowY: 'auto', WebkitOverflowScrolling: 'touch', flex: 1 }}>

          {/* Drop zone */}
          <div
            className={`dropzone${dragOver ? ' dragover' : ''}`}
            style={{ marginBottom: 16 }}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="dropzone__icon"><Icon name="paperclip" size={26} /></div>
            {file ? (
              <div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{file.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{formatFileSize(file.size)}</div>
              </div>
            ) : (
              <div className="dropzone__text">
                <strong>Click to select</strong> or drag & drop<br />
                <span style={{ fontSize: 12 }}>PDF, JPG, PNG — up to {MAX_FILE_SIZE_MB}MB</span>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.bmp,.tif,.tiff,.txt"
            style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])}
          />

          <div style={{ marginBottom: 10 }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCapturing(true)}>Take a photo of a paper report</button>
          </div>
          {capturing && <PhotoCapture onClose={() => setCapturing(false)} onCapture={(f) => { setCapturing(false); handleFile(f); }} />}
          <PhotoQualityNotice problems={photoProblems} file={file} recordType={recordType} />

          {/* Record type grid — FIX: 3 cols on desktop, 3 cols on mobile (compact) */}
          <div style={{ marginBottom: 16 }}>
            <label className="form-label" style={{ display: 'block', marginBottom: 8 }}>Document Type</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {RECORD_TYPES.map(t => (
                <button type="button" key={t.value} onClick={() => setRecordType(t.value)} style={{
                  padding: '8px 4px',
                  border: `1.5px solid ${recordType === t.value ? 'var(--brand-dark)' : 'var(--border)'}`,
                  borderRadius: 8,
                  background: recordType === t.value ? 'var(--brand-dark)' : 'var(--bg-hover)',
                  color: recordType === t.value ? 'var(--text-on-brand-dark)' : 'var(--text-secondary)',
                  fontSize: 11, fontWeight: recordType === t.value ? 700 : 500,
                  cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                  transition: 'all 0.15s',
                }}>
                  <span style={{ fontSize: 16 }}>{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Date + Lab — FIX: stack on very small screens */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 14 }}>
            <div>
              <label className="form-label">Test / Visit Date *</label>
              <input type="date" className="form-input" value={drawDate}
                onChange={e => setDrawDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                style={{ width: '100%' }} />
            </div>
            <div>
              <label className="form-label">Lab / Hospital</label>
              <input type="text" className="form-input" placeholder="e.g. Metropolis Labs"
                value={labName} onChange={e => setLabName(e.target.value)}
                style={{ width: '100%' }} />
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Notes (optional)</label>
            <textarea className="form-textarea" style={{ minHeight: 60, width: '100%', boxSizing: 'border-box' }}
              placeholder="Any context the navigator should know..."
              value={notes} onChange={e => setNotes(e.target.value)} />
          </div>

          {/* Error */}
          {error && (
            <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12, padding: '8px 12px', background: 'var(--red-bg)', borderRadius: 7 }}>
              ⚠ {error}
            </div>
          )}

          {/* FIX: Buttons always visible at bottom of content, not cut off */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{
              flex: 1, padding: '12px', background: 'var(--bg-subtle)', border: 'none',
              borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)',
            }}>Cancel</button>
            <button type="button" onClick={handleSubmit} disabled={uploading} style={{
              flex: 2, padding: '12px', background: 'var(--brand-dark)', border: 'none',
              borderRadius: 8, cursor: uploading ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 700, color: 'var(--text-on-brand-dark)', opacity: uploading ? 0.7 : 1,
            }}>
              {uploading ? 'Uploading…' : '⬆ Upload Document'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Portal renders modal into document.body — bypasses any parent overflow/clip
  return createPortal(modalContent, document.body);
}

// ── Record Row ────────────────────────────────────────────────────────────────

function RecordRow({ record, onDownload, onDelete, onShowReport, onRerunTriage, isRerunning, onReupload, onUpdateMeta, onRetryTriage, isRetrying, onResolveMismatch, isResolvingMismatch, triageBusy }) {
  const [downloading, setDownloading] = useState(false);
  const [editing,     setEditing]     = useState(false);
  const [editDate,    setEditDate]    = useState(record.drawDate || '');
  const [editLab,     setEditLab]     = useState(record.labName  || '');
  const [saving,      setSaving]      = useState(false);
  const typeConfig = getTypeConfig(record.recordType);
  // Extraction / triage is running: block actions that would start a second run, edit or remove the record under it.
  const busy = triageBusy || SERVER_RUNNING.includes(record.processingStatus);
  const busyTitle = 'Triage is in progress. Please wait for it to finish.';

  const handleDownload = async () => {
    setDownloading(true);
    try { await onDownload(record); } finally { setDownloading(false); }
  };

  const handleSaveMeta = async () => {
    setSaving(true);
    try {
      await onUpdateMeta(record, { drawDate: editDate || null, labName: editLab || null });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditDate(record.drawDate || '');
    setEditLab(record.labName  || '');
    setEditing(false);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 14px', borderBottom: '1px solid var(--border)',
      transition: 'background 0.15s',
      // FIX: wrap on very small screens
      flexWrap: 'wrap',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = ''}
    >
      {/* Icon */}
      <div style={{
        width: 38, height: 38, borderRadius: 9, background: 'var(--bg-subtle)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 17, flexShrink: 0,
      }}>
        {typeConfig.icon}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 3 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>
            {typeConfig.label}
          </span>
          {!SERVER_RUNNING.includes(record.processingStatus) && <StatusChip status={record.processingStatus} />}
          {record.latestTriage && <RiskBadge triage={record.latestTriage} />}
          {record.latestTriage?.hasAcuteAlert && (
            <span style={{ fontSize: 11, color: 'var(--red)', fontWeight: 700 }}>ACUTE ALERT</span>
          )}
        </div>
        {/* FIX: meta row wraps properly on mobile */}
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span>{formatDate(record.drawDate)}</span>
          {record.labName && <span>{record.labName}</span>}
          {record.fileSizeBytes && <span>{formatFileSize(record.fileSizeBytes)}</span>}
          {record.uploadedByName && <span>{record.uploadedByName}</span>}
        </div>
        {record.notes && (
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3, fontStyle: 'italic' }}>
            {record.notes}
          </div>
        )}
        <HeldReportNotice record={record} reviewHref={`/quality-check/${record.id}`} />
        {['EXTRACTED', 'EXTRACTING'].includes(record.processingStatus) && record.triageFailureReason && record.triageAttemptCount > 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--amber-text)', marginTop: 3 }}>
            The last triage attempt did not complete ({record.triageAttemptCount} of 3): {record.triageFailureReason}. It will be retried automatically, or press Run Triage.
          </div>
        )}
        {record.processingStatus === 'FAILED' && record.triageFailureReason && (
          <div style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 3 }}>
            Triage failed{record.triageAttemptCount ? ` after ${record.triageAttemptCount} attempt(s)` : ''}: {record.triageFailureReason}
          </div>
        )}
      </div>

      {/* Actions — never wrap on desktop, always on one line; on phones
           .doc-actions moves them to their own full-width line and wraps */}
      <div className="doc-actions" style={{ gap: 6, flexShrink: 0 }}>
        {record.latestTriage && (
          <button type="button" onClick={() => onShowReport(record)} style={{
            padding: '6px 10px', border: '1px solid var(--brand-accent)',
            borderRadius: 7, background: 'var(--bg-surface)', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, color: 'var(--brand-accent)',
            whiteSpace: 'nowrap',
          }}>
            View Triage
          </button>
        )}
        {/* ── Triage action buttons — blood tests only ──────────────────────
             TRIAGED + hasExtractedText  → Re-run (regenerate with updated logic)
             TRIAGED + no text           → nothing (View Triage is enough)
             EXTRACTED/NEEDS_REVIEW + text → Run Triage (first triage failed)
             EXTRACTED/NEEDS_REVIEW + no text → Re-upload (extraction incomplete)
             UPLOADED                    → nothing
        */}
        {record.recordType === 'BLOOD_TEST' && (() => {
          const isTr  = record.processingStatus === 'TRIAGED';
          const isExt = ['EXTRACTED', 'NEEDS_MANUAL_REVIEW'].includes(record.processingStatus);
          const hasTxt = record.hasExtractedText;
          const btnStyle = (color, bg, busy = isRerunning) => ({
            padding: '6px 10px', borderRadius: 7, cursor: busy ? 'not-allowed' : 'pointer',
            border: `1px solid ${color}`, background: busy ? bg : 'var(--bg-surface)',
            fontSize: 12, fontWeight: 600, color, opacity: busy ? 0.7 : 1, whiteSpace: 'nowrap',
          });

          if ((isTr || isExt) && hasTxt) return (
            <button type="button" onClick={() => onRerunTriage(record)} disabled={isRerunning || busy}
              title={busy ? busyTitle : isTr ? 'Regenerate triage with updated clinical logic' : 'Run triage on this report'}
              style={btnStyle('var(--green)', 'var(--green-bg)', isRerunning || busy)}>
              {isRerunning ? 'Running…' : isTr ? 'Re-run' : 'Run Triage'}
            </button>
          );

          if (isExt && !hasTxt) return (
            <button type="button" onClick={() => onReupload(record)} disabled={busy}
              title={busy ? busyTitle : 'Extraction incomplete — re-upload the PDF'}
              style={btnStyle('var(--amber)', 'var(--amber-bg)')}>
              ↑ Re-upload
            </button>
          );

          // FAILED: the server-side auto-retry safety net (TriageAutoRetryScheduler)
          // already burned its 3 attempts — this is the only way forward without
          // re-uploading, and unlike the buttons above it never touches that
          // scheduler's own attempt count, so it stays available indefinitely.
          if (record.processingStatus === 'FAILED') return (
            <button type="button" onClick={() => onRetryTriage(record)} disabled={isRetrying || busy}
              title={busy ? busyTitle : 'Retry server-side triage'}
              style={btnStyle('var(--red)', 'var(--red-bg)', isRetrying || busy)}>
              {isRetrying ? 'Retrying…' : 'Retry'}
            </button>
          );

          // EXTRACTING: the ONLY way this ever advances on its own is either
          // pdf.js finishing in a live browser tab (never happens for a
          // backend-created record — webhook import, pull-report) or
          // TriageAutoRetryScheduler picking it up, which only starts once
          // the record is 5+ minutes old and re-checks every 5 minutes after
          // that — so a record can sit here for a real stretch with no
          // feedback and, until now, no way to force it. Same endpoint as
          // the FAILED button below; retryTriage() (re-)extracts text and
          // runs triage regardless of which status it's called from.
          if (record.processingStatus === 'EXTRACTING') return (
            <button type="button" onClick={() => onRetryTriage(record)} disabled={isRetrying || busy}
              title={busy ? busyTitle : 'Run triage now instead of waiting for the automatic retry'}
              style={btnStyle('var(--amber)', 'var(--amber-bg)', isRetrying || busy)}>
              {isRetrying ? 'Running…' : '▶ Run Triage'}
            </button>
          );

          // AWAITING_IDENTITY_CONFIRMATION: previously only resolvable by the
          // browser session that was actively uploading/re-running it (a live
          // React state, autoTriageStage/identityMismatch) — nothing brought
          // this modal back for a record that arrived at this status any
          // other way, including via TriageAutoRetryScheduler. This button
          // re-runs the same server-side identity check the live flow uses
          // and reopens the confirmation modal from its result.
          if (record.processingStatus === 'AWAITING_IDENTITY_CONFIRMATION') return (
            <button type="button" onClick={() => onResolveMismatch(record)} disabled={isResolvingMismatch}
              title="Review and confirm this report's identity"
              style={btnStyle('var(--red)', 'var(--red-bg)', isResolvingMismatch)}>
              {isResolvingMismatch ? 'Checking…' : 'Resolve'}
            </button>
          );

          return null;
        })()}
        <button type="button" onClick={handleDownload} disabled={downloading} style={{
          padding: '6px 10px', border: '1px solid var(--border)',
          borderRadius: 7, background: 'var(--bg-surface)', cursor: 'pointer',
          fontSize: 12, fontWeight: 600, color: 'var(--text-primary)',
          opacity: downloading ? 0.6 : 1, whiteSpace: 'nowrap',
        }}>
          {downloading ? 'Preparing…' : '⬇ Download'}
        </button>
        <button type="button" onClick={() => setEditing(e => !e)} disabled={busy}
          title={busy ? busyTitle : 'Edit draw date or lab name'}
          aria-label="Edit record"
          style={{
            padding: '6px 10px', border: '1px solid var(--border)',
            borderRadius: 7, background: editing ? 'var(--bg-canvas)' : 'var(--bg-surface)',
            cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
            whiteSpace: 'nowrap',
          }}>✎ Edit</button>
        <button type="button" onClick={() => onDelete(record)} disabled={busy} title={busy ? busyTitle : 'Delete'}
          aria-label="Delete record" style={{
          padding: '6px 8px', border: '1px solid var(--red)',
          borderRadius: 7, background: 'var(--bg-surface)', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1,
          fontSize: 12, color: 'var(--red)', whiteSpace: 'nowrap',
        }}>Delete</button>
      </div>

      {/* Inline edit panel — draw date + lab name */}
      {editing && (
        <div style={{
          width: '100%', marginTop: 10, padding: '12px 14px',
          background: 'var(--bg-subtle)', borderRadius: 8,
          border: '1px solid var(--bg-hover)',
          display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end',
        }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600,
              color: 'var(--text-secondary)', marginBottom: 4, fontFamily: 'var(--font-body)' }}>
              Draw Date
            </label>
            <input
              type="date"
              className="form-input"
              value={editDate}
              max={new Date().toISOString().split('T')[0]}
              onChange={e => setEditDate(e.target.value)}
              style={{ fontSize: 12, padding: '5px 9px', width: 150 }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600,
              color: 'var(--text-secondary)', marginBottom: 4, fontFamily: 'var(--font-body)' }}>
              Lab / Hospital
            </label>
            <input
              type="text"
              className="form-input"
              value={editLab}
              placeholder="e.g. Metropolis Labs"
              onChange={e => setEditLab(e.target.value)}
              style={{ fontSize: 12, padding: '5px 9px', width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={handleCancelEdit} style={{
              padding: '6px 12px', border: '1px solid var(--bg-hover)',
              borderRadius: 7, background: 'var(--bg-surface)', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
              fontFamily: 'var(--font-body)',
            }}>Cancel</button>
            <button type="button" onClick={handleSaveMeta} disabled={saving} style={{
              padding: '6px 14px', border: 'none',
              borderRadius: 7, background: 'var(--brand-dark)', cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: 12, fontWeight: 600, color: 'var(--text-on-brand-dark)', opacity: saving ? 0.7 : 1,
              fontFamily: 'var(--font-body)',
            }}>{saving ? 'Saving…' : '✓ Save'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function HealthRecordsTab({ patient, autoOpenRecordId = null }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [filterType, setFilterType] = useState('ALL');
  const [error, setError] = useState('');

  const navigate = useNavigate();

  // Auto-triage: tracks current triaging record + pipeline stage
  const [triagingRecordId, setTriagingRecordId] = useState(null);
  // Re-run triage: tracks which existing record is being re-triaged
  const [rerunningRecordId, setRerunningRecordId] = useState(null);
  // V108: retrying server-side triage on a record stuck at FAILED — a
  // different action from Re-run above (that reruns the CLIENT pipeline on
  // stored extractedText; this calls the new backend retry-triage endpoint,
  // which also (re-)extracts text server-side if none is stored).
  const [retryingRecordId, setRetryingRecordId] = useState(null);
  // Resolving an identity mismatch on an EXISTING record already sitting at
  // AWAITING_IDENTITY_CONFIRMATION — distinct from the identityMismatch/
  // confirmIdentityMismatch state below, which belongs to useAutoTriage's
  // live upload/rerun pipeline and only exists while that pipeline is
  // actively running in this tab. A record can reach AWAITING_IDENTITY_
  // CONFIRMATION with no pipeline running at all — e.g. via
  // TriageAutoRetryScheduler (V109) picking it up long after the browser
  // that uploaded it is gone — so resolving it needs its own self-contained
  // flow, not a resume of a pipeline that was never started in this session.
  const [resolvingMismatchRecordId, setResolvingMismatchRecordId] = useState(null);
  const [mismatchToResolve, setMismatchToResolve] = useState(null); // { record, extractedText, data }
  const {
    stage: autoTriageStage, triageError: autoTriageError, isTriaging,
    identityMismatch, runAutoTriage, confirmIdentityMismatch, cancelIdentityMismatch,
    reset: resetTriage,
  } = useAutoTriage();

  // A triage this tab started before the navigator switched away is still running in the background (see useInflightTriage); adopt
  // it on return so its progress bar shows again. When this instance started the run itself, its own state stays authoritative.
  const inflight = useInflightTriage();
  const adoptedRun = !triagingRecordId && inflight ? inflight : null;
  const bannerRecordId = triagingRecordId || adoptedRun?.recordId || null;
  const bannerStage = triagingRecordId ? autoTriageStage : adoptedRun?.stage;
  const triageClearTimer = useRef(null);

  // Cleanup timer on unmount to prevent setState-after-unmount warning
  useEffect(() => {
    return () => {
      if (triageClearTimer.current) clearTimeout(triageClearTimer.current);
    };
  }, []);

  const patientId = patient?.id;

  const loadRecords = useCallback(async () => {
    if (!patientId) return;
    try {
      const res = await healthRecordAPI.list(patientId);
      setRecords(res.data);
    } catch {
      setError('Failed to load health records');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  // The adopted run settled (or moved on to another record): refresh so the row shows its result.
  const adoptedRecordId = adoptedRun?.recordId;
  useEffect(() => {
    if (!adoptedRecordId) return undefined;
    return () => { loadRecords(); };
  }, [adoptedRecordId, loadRecords]);

  // While the server is triaging any record, keep the list fresh so the row turns into its new result by itself. Stops after about ten
  // minutes (a record stuck there is the retry scheduler's to deal with, not worth polling for ever).
  const serverRunning = records.some(r => SERVER_RUNNING.includes(r.processingStatus) && r.id !== bannerRecordId);
  useEffect(() => {
    if (!serverRunning) return undefined;
    let ticks = 0;
    const timer = setInterval(() => {
      ticks += 1;
      if (ticks > 120) { clearInterval(timer); return; }
      loadRecords();
    }, 5000);
    return () => clearInterval(timer);
  }, [serverRunning, loadRecords]);

  // Pre-Call Briefing links here with a recordId; the report is its own page,
  // so forward there (replace, so Back doesn't bounce into this redirect).
  useEffect(() => {
    if (!autoOpenRecordId || !patientId) return;
    navigate(`/patients/${patientId}/reports/${autoOpenRecordId}`, { replace: true });
  }, [autoOpenRecordId, patientId, navigate]);

  /** Applies a settled (DONE/FAILED/SKIPPED) triage outcome — updates the
   * record in state and starts the 3s auto-clear. Never called while
   * AWAITING_IDENTITY_CONFIRMATION — see handleUploaded/handleConfirmUploadIdentity. */
  const applyTriageOutcome = useCallback((recordId, triageResult) => {
    if (triageResult) {
      setRecords(prev => prev.map(r =>
        r.id === recordId
          ? {
              ...r,
              processingStatus: 'TRIAGED',
              latestTriage: {
                id:                  triageResult.id,
                consensusRiskLevel:  triageResult.consensusRiskLevel,
                consensusRiskScore:  triageResult.consensusRiskScore,
                hasAcuteAlert:       triageResult.hasAcuteAlert,
                acuteAlertDetail:    triageResult.acuteAlertDetail,
                createdAt:           triageResult.createdAt,
              },
            }
          : r
      ));
    }
    if (triageClearTimer.current) clearTimeout(triageClearTimer.current);
    triageClearTimer.current = setTimeout(() => {
      setTriagingRecordId(null);
      resetTriage();
    }, 3000);
  }, [resetTriage]);

  // Bug fix: isTriaging deliberately excludes AWAITING_IDENTITY_CONFIRMATION
  // (it's "paused for the user", not "actively working"), so a check against
  // isTriaging alone let a second upload/re-run start the instant the
  // identity-mismatch modal appeared — before the user answered it — because
  // handleUploaded's/handleRerunTriage's unconditional resetTriage() call
  // wiped the first pending confirmation out from under the still-open modal.
  const pipelineBusy = isTriaging || !!adoptedRun || autoTriageStage === 'AWAITING_IDENTITY_CONFIRMATION';

  const handleUploaded = async (newRecord, file) => {
    // Add record to list immediately so user sees it right away
    setRecords(prev => [newRecord, ...prev]);

    // Only auto-triage blood test PDFs and photos
    const isBloodTestFile = newRecord.recordType === 'BLOOD_TEST'
      && (newRecord.mimeType === 'application/pdf' || isScanImage(newRecord.mimeType));
    if (!isBloodTestFile) return;

    setTriagingRecordId(newRecord.id);
    resetTriage();

    const { status, triageResult, record: serverRecord } = await runAutoTriage(newRecord, file, patient, patientId);

    // A scan was handed to the server's scan reader, which returns the record in its new state (with latestTriage once triaged).
    if (serverRecord) {
      setRecords(prev => prev.map(r => (r.id === newRecord.id ? serverRecord : r)));
    }

    // DEF-023 follow-up: paused for confirmation — the modal (rendered
    // below, off `identityMismatch`) takes over. Don't start the auto-clear
    // timer, which would wipe the pending confirmation state from under it.
    if (status === 'AWAITING_IDENTITY_CONFIRMATION') return;

    applyTriageOutcome(newRecord.id, triageResult);
  };

  const handleConfirmUploadIdentity = useCallback(async (comment) => {
    const recordId = triagingRecordId;
    const { triageResult } = await confirmIdentityMismatch(comment);
    applyTriageOutcome(recordId, triageResult);
  }, [triagingRecordId, confirmIdentityMismatch, applyTriageOutcome]);

  const handleCancelUploadIdentity = useCallback(() => {
    cancelIdentityMismatch();
    setTriagingRecordId(null);
  }, [cancelIdentityMismatch]);

  const handleShowReport = useCallback((record) => {
    if (!record.latestTriage) return;
    navigate(`/patients/${patientId}/reports/${record.id}`);
  }, [navigate, patientId]);

  const handleDownload = async (record) => {
    try {
      const res = await healthRecordAPI.getDownloadUrl(patientId, record.id);
      window.open(res.data.downloadUrl, '_blank', 'noopener');
    } catch {
      alert('Failed to generate download link. Please try again.');
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Delete "${record.fileName}"? This cannot be undone.`)) return;
    try {
      await healthRecordAPI.delete(patientId, record.id);
      setRecords(prev => prev.filter(r => r.id !== record.id));
      // If this record was being triaged, clear the triage indicator
      if (triagingRecordId === record.id) {
        if (triageClearTimer.current) clearTimeout(triageClearTimer.current);
        setTriagingRecordId(null);
        resetTriage();
      }
    } catch {
      setError('Failed to delete record');
    }
  };

  /**
   * Open the upload modal pre-populated with this record's metadata for re-upload.
   * Used when extractedText is missing (triage failed before saveExtractedText was called).
   * Currently opens the upload modal — navigator re-selects the file and submits.
   */
  const handleReupload = useCallback((_record) => {
    if (pipelineBusy) return; // don't clobber a pending identity confirmation
    // Open the upload modal — navigator re-uploads the same file
    // Future: pre-populate recordType and drawDate from _record
    setShowUpload(true);
  }, [pipelineBusy]);

  /**
   * Update draw date and/or lab name on an existing health record.
   * Uses PATCH /patients/{patientId}/health-records/{recordId}.
   * Updates the record in local state on success — no full reload needed.
   */
  const handleUpdateMeta = useCallback(async (record, { drawDate, labName }) => {
    try {
      await healthRecordAPI.update(patientId, record.id, { drawDate, labName });
      setRecords(prev => prev.map(r =>
        r.id === record.id
          ? { ...r, drawDate: drawDate || r.drawDate, labName: labName || r.labName }
          : r
      ));
    } catch {
      setError('Failed to update record — please try again.');
    }
  }, [patientId]);

  /**
   * Re-run triage on an existing health record using its stored extractedText.
   * Uses the same runAutoTriage pipeline as the initial upload.
   * Available only for BLOOD_TEST records that have extractedText stored.
   */
  const handleRerunTriage = useCallback(async (record) => {
    if (rerunningRecordId) return; // prevent double-fire
    if (pipelineBusy) return; // don't clobber a pending identity confirmation

    setRerunningRecordId(record.id);
    setError('');
    try {
      // The server marks the record as running and triages it itself; the row then shows "Triage in progress" (see the polling effect
      // above) and keeps showing it if the navigator closes the tab or comes back later. The existing report stays until the new one
      // is saved. Re-run supplies no new text, so the identity check never fires here.
      const res = await healthRecordAPI.rerunTriage(patientId, record.id);
      setRecords(prev => prev.map(r => (r.id === record.id ? { ...r, ...res.data, latestTriage: res.data.latestTriage || r.latestTriage } : r)));
    } catch (err) {
      setError(`Re-run triage failed: ${err.response?.data?.message || err.message || 'Please try again.'}`);
    } finally {
      setRerunningRecordId(null);
    }
  }, [rerunningRecordId, pipelineBusy, patientId]);

  /**
   * Retries server-side triage (TriageAutoRetryScheduler's manual escape
   * hatch) for a record stuck at FAILED. Unlike Re-run above, this doesn't
   * need extractedText client-side — the backend (re-)extracts it if
   * missing and runs Claude itself — so it's available regardless of
   * hasExtractedText.
   */
  const handleRetryTriage = useCallback(async (record) => {
    if (retryingRecordId) return; // prevent double-fire
    setRetryingRecordId(record.id);
    setError('');
    try {
      const res = await healthRecordAPI.retryTriage(patientId, record.id);
      setRecords(prev => prev.map(r => (r.id === record.id ? res.data : r)));
    } catch (err) {
      setError(`Retry triage failed: ${err.message || 'Please try again.'}`);
    } finally {
      setRetryingRecordId(null);
    }
  }, [retryingRecordId, patientId]);

  /**
   * Opens the identity-mismatch resolution flow for a record already stuck
   * at AWAITING_IDENTITY_CONFIRMATION with no live upload/rerun in progress.
   * Re-submits the record's own stored extractedText through the same
   * saveExtractedText endpoint the live pipeline uses — this re-runs the
   * backend's identity check fresh and is expected to 409 again (nothing
   * about the record or the patient's profile has changed), which is
   * exactly what hands back the extracted-vs-profile details to show.
   */
  const handleOpenResolveMismatch = useCallback(async (record) => {
    if (resolvingMismatchRecordId) return;
    setResolvingMismatchRecordId(record.id);
    setError('');
    let extractedText = null;
    try {
      const fullRecord = await healthRecordAPI.get(patientId, record.id);
      extractedText = fullRecord.data.extractedText;
      if (!extractedText) {
        setError('No extracted text found for this record — please re-upload the PDF.');
        return;
      }
      const res = await healthRecordAPI.saveExtractedText(patientId, record.id, extractedText);
      // No 409 this time — e.g. the patient's profile was corrected since.
      // Nothing left to resolve; just reflect the record's new status.
      setRecords(prev => prev.map(r => (r.id === record.id ? res.data : r)));
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.error === 'IDENTITY_MISMATCH') {
        setMismatchToResolve({ record, extractedText, data: err.response.data });
      } else {
        setError('Could not check this record — please try again.');
      }
    } finally {
      setResolvingMismatchRecordId(null);
    }
  }, [resolvingMismatchRecordId, patientId]);

  const handleConfirmResolveMismatch = useCallback(async (comment) => {
    const target = mismatchToResolve;
    if (!target) return;
    const res = await healthRecordAPI.saveExtractedText(
      patientId, target.record.id, target.extractedText, true, comment
    );
    setRecords(prev => prev.map(r => (r.id === target.record.id ? res.data : r)));
    setMismatchToResolve(null);
    // Confirming only saves the text (the record becomes "Ready"); start triage now instead of leaving it for a manual "Run Triage".
    // A report the server held for a quality check (several dated columns) stays held and is triaged when the navigator releases it.
    if (res.data?.processingStatus === 'EXTRACTED') handleRerunTriage(res.data);
  }, [mismatchToResolve, patientId, handleRerunTriage]);

  const handleCancelResolveMismatch = useCallback(() => {
    setMismatchToResolve(null);
  }, []);

  const filteredRecords = filterType === 'ALL'
    ? records
    : records.filter(r => r.recordType === filterType);

  const grouped = filteredRecords.reduce((acc, r) => {
    const key = r.drawDate || 'No date';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  const sortedDates = Object.keys(grouped).sort((a, b) => {
    if (a === 'No date') return 1;
    if (b === 'No date') return -1;
    return new Date(b) - new Date(a);
  });

  if (loading) {
    return (
      <div className="loading-state" style={{ padding: 40 }}>
        <div className="dot-loader"><span /><span /><span /></div>
        <div style={{ fontSize: 13 }}>Loading health records…</div>
      </div>
    );
  }

  return (
    <div>
      {showUpload && (
        <UploadModal
          patientId={patientId}
          patient={patient}
          onClose={() => setShowUpload(false)}
          onUploaded={handleUploaded}
        />
      )}


      {autoTriageStage === 'AWAITING_IDENTITY_CONFIRMATION' && (
        <IdentityMismatchModal
          mismatch={identityMismatch}
          onConfirm={handleConfirmUploadIdentity}
          onCancel={handleCancelUploadIdentity}
        />
      )}

      {/* Resolving a mismatch on an existing record — no live pipeline running,
          see handleOpenResolveMismatch's comment for why this is separate
          from the modal instance above. */}
      {mismatchToResolve && (
        <IdentityMismatchModal
          mismatch={{
            extractedName: mismatchToResolve.data.extractedName,
            extractedAge: mismatchToResolve.data.extractedAge,
            profileName: mismatchToResolve.data.profileName,
            profileAge: mismatchToResolve.data.profileAge,
          }}
          title="Confirm this report's identity"
          description="This report's own details still don't match the patient's profile. Confirm to proceed with triage anyway, or close this and re-upload/delete the document instead."
          cancelLabel="Not now"
          onConfirm={handleConfirmResolveMismatch}
          onCancel={handleCancelResolveMismatch}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--brand-accent)', marginBottom: 1 }}>
            CLINICAL DOCUMENTS
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--text-primary)' }}>
            Health Records
          </div>
        </div>
        <button type="button" onClick={() => !pipelineBusy && setShowUpload(true)} disabled={pipelineBusy} style={{
          padding: '9px 18px', background: 'var(--brand-dark)', color: 'var(--text-on-brand-dark)',
          border: 'none', borderRadius: 8, cursor: pipelineBusy ? 'not-allowed' : 'pointer',
          opacity: pipelineBusy ? 0.5 : 1,
          fontSize: 13, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          + Upload Document
        </button>
      </div>

      {error && (
        <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12, padding: '8px 12px', background: 'var(--red-bg)', borderRadius: 7 }}>
          ⚠ {error}
        </div>
      )}

      {/* Filter tabs */}
      {records.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          <button type="button"
            onClick={() => setFilterType('ALL')}
            style={{
              padding: '6px 14px', borderRadius: 100, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 600,
              background: filterType === 'ALL' ? 'var(--brand-dark)' : 'var(--bg-canvas)',
              color: filterType === 'ALL' ? 'var(--text-on-brand-dark)' : 'var(--text-secondary)',
            }}>
            All ({records.length})
          </button>
          {RECORD_TYPES.filter(t => records.some(r => r.recordType === t.value)).map(t => (
            <button type="button" key={t.value}
              onClick={() => setFilterType(t.value)}
              style={{
                padding: '6px 14px', borderRadius: 100, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600,
                background: filterType === t.value ? 'var(--brand-dark)' : 'var(--bg-canvas)',
                color: filterType === t.value ? 'var(--text-on-brand-dark)' : 'var(--text-secondary)',
              }}>
              {t.icon} {t.label} ({records.filter(r => r.recordType === t.value).length})
            </button>
          ))}
        </div>
      )}

      {/* Empty state */}
      {records.length === 0 && (
        /* The shared component, rather than .lt-result-empty's own emoji +
           two divs + a hand-rolled navy button. Same three parts, one
           implementation, and it matches every other empty state in the app. */
        <EmptyState
          icon="folder"
          title="No health records yet"
          message="Upload the first document — blood tests, X-rays, prescriptions, or any clinical file."
          action={pipelineBusy ? undefined : {
            label: '+ Upload First Document',
            onClick: () => setShowUpload(true),
          }}
          framed
        />
      )}

      {/* Timeline */}
      {sortedDates.map(date => (
        <div key={date} style={{ marginBottom: 20 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '1px', color: 'var(--text-muted)',
            fontFamily: "'DM Sans', sans-serif",
            padding: '4px 0 8px',
            borderBottom: '2px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Icon name="calendar" size={13} />
            {date === 'No date' ? 'Date unknown' : formatDate(date)}
          </div>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {grouped[date].map(record => (
              <div key={record.id}>
                <RecordRow
                  record={record}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                  onShowReport={handleShowReport}
                  onRerunTriage={handleRerunTriage}
                  isRerunning={rerunningRecordId === record.id}
                  onReupload={handleReupload}
                  onUpdateMeta={handleUpdateMeta}
                  onRetryTriage={handleRetryTriage}
                  isRetrying={retryingRecordId === record.id}
                  onResolveMismatch={handleOpenResolveMismatch}
                  isResolvingMismatch={resolvingMismatchRecordId === record.id}
                  triageBusy={bannerRecordId === record.id && (triagingRecordId ? isTriaging : !!adoptedRun)}
                />
                {SERVER_RUNNING.includes(record.processingStatus) && bannerRecordId !== record.id && (
                  <div style={{ padding: '0 14px 10px' }}><ServerTriageBanner /></div>
                )}
                {bannerRecordId === record.id && (
                  <div style={{ padding: '0 14px 10px' }}>
                    <TriageProgressBanner
                      recordId={record.id}
                      stage={bannerStage}
                      triageError={autoTriageError}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Footer stats */}
      {records.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 8 }}>
          {records.length} document{records.length !== 1 ? 's' : ''}
          {records.filter(r => r.processingStatus === 'TRIAGED').length > 0 && (
            <> · {records.filter(r => r.processingStatus === 'TRIAGED').length} triaged</>
          )}
          {records.filter(r => r.processingStatus === 'NEEDS_MANUAL_REVIEW').length > 0 && (
            <> · <span style={{ color: 'var(--red)', fontWeight: 600 }}>
              {records.filter(r => r.processingStatus === 'NEEDS_MANUAL_REVIEW').length} need review
            </span></>
          )}
          {records.filter(r => r.processingStatus === 'FAILED').length > 0 && (
            <> · <span style={{ color: 'var(--red)', fontWeight: 600 }}>
              {records.filter(r => r.processingStatus === 'FAILED').length} triage failed
            </span></>
          )}
        </div>
      )}
    </div>
  );
}
